import {computed, onMounted, onUnmounted, ref} from "vue";
import dayjs from "dayjs";
import {storeToRefs} from "pinia";
import dateHelper from "@/core/helpers/dateHelper";
import {useSystemStore} from "@/stores/system.store";

/** How long a pharmacy may go without an import before the alert is raised. */
const STALE_AFTER_DAYS = 7;

/** How long the loaded environment is trusted before it is read again. */
const REFRESH_INTERVAL = 15 * 60 * 1000;

/** How often the elapsed time is evaluated again, free of any request. */
const CLOCK_INTERVAL = 60 * 60 * 1000;

/**
 * The clock the staleness is evaluated against.
 *
 * Time has to be a reactive input, or it would be captured on the first
 * evaluation and the alert would never appear on a window nobody touches.
 */
const now = ref(Date.now());

/**
 * Started once per window and never stopped: a surface unmounting must not
 * freeze the state of the ones still showing it.
 */
let clock: ReturnType<typeof setInterval> | null = null;

interface Options {
    /** Read the environment again when this surface mounts. */
    refreshOnMount?: boolean;

    /** Read it again when the window regains focus, for surfaces that stay on screen for hours. */
    watchWindowFocus?: boolean;
}

/**
 * The state of the LGO synchronisation, kept from going stale.
 *
 * It falls both ways, and each way needs its own trigger: the alert appears
 * once enough time has passed without a sync, which only the clock reveals
 * since the absence of an import is not an event, and it disappears once a
 * sync came back, which only a fresh read reveals.
 */
const useLgoSyncState = (options: Options = {}) => {
    const systemStore = useSystemStore();
    const {environment} = storeToRefs(systemStore);

    /** The date of the last import, as the pharmacist reads it. */
    const lastSyncDate = computed(() => {
        const lastImport = environment.value?.entity?.lastLgoImport;

        if (!lastImport) {
            return null;
        }

        return dateHelper.shortFormat(lastImport, "DD MMMM YYYY à HH:mm");
    });

    /** A pharmacy that never imported anything answers false: it has no sync to be stale. */
    const isSyncStale = computed(() => {
        const lastImport = environment.value?.entity?.lastLgoImport;

        if (!lastImport) {
            return false;
        }

        return dayjs(lastImport).add(STALE_AFTER_DAYS, 'day').isBefore(dayjs(now.value));
    });

    /** Read the environment again, unless it was read recently. A failure does not consume the budget. */
    const refresh = (): Promise<void> => {
        const loadedAt = systemStore.environmentLoadedAt;

        if (loadedAt !== null && Date.now() - loadedAt < REFRESH_INTERVAL) {
            return Promise.resolve();
        }

        return systemStore.refreshEnvironment()
            .then(() => undefined)
            .catch(() => undefined);
    };

    /** Bring the state up to date in both directions. */
    const evaluate = (): Promise<void> => {
        now.value = Date.now();

        return refresh();
    };

    const evaluateWhenVisible = () => {
        if (document.visibilityState === 'visible') {
            evaluate();
        }
    };

    onMounted(() => {
        if (!clock) {
            clock = setInterval(() => {
                now.value = Date.now();
            }, CLOCK_INTERVAL);
        }

        if (options.refreshOnMount) {
            evaluate();
        }

        if (options.watchWindowFocus) {
            window.addEventListener('focus', evaluate);
            document.addEventListener('visibilitychange', evaluateWhenVisible);
        }
    });

    onUnmounted(() => {
        if (options.watchWindowFocus) {
            window.removeEventListener('focus', evaluate);
            document.removeEventListener('visibilitychange', evaluateWhenVisible);
        }
    });

    return {isSyncStale, lastSyncDate, evaluate};
};

export default useLgoSyncState;
